import * as DialogPrimitive from '@radix-ui/react-dialog';

import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

import { SmartButton } from './smartButton';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogCancel = React.forwardRef<
  React.ElementRef<typeof SmartButton>,
  React.ComponentPropsWithoutRef<typeof SmartButton>
>(({ className, ...props }, ref) => (
  <SmartButton
    color="neutral"
    ref={ref}
    shortcuts={{ key: 'escape' }}
    type="reset"
    variant="outline"
    {...props}
  />
));
DialogCancel.displayName = 'DialogCancel';

const DialogSubmit = React.forwardRef<
  React.ElementRef<typeof SmartButton>,
  React.ComponentPropsWithoutRef<typeof SmartButton>
>(({ className, ...props }, ref) => (
  <SmartButton
    ref={ref}
    shortcuts={{ key: 'enter', ctrl: true }}
    variant="default"
    {...props}
  />
));
DialogSubmit.displayName = 'DialogSubmit';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/15 data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    ref={ref}
    {...props}
    onClick={e => e.stopPropagation()}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 flex max-h-[calc(100vh-15rem)] w-full max-w-3xl translate-x-[-50%] translate-y-[-45%] flex-col border bg-background shadow-gray-600/30 shadow-xl outline-none duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg',
        className
      )}
      ref={ref}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex shrink-0 flex-col space-y-1.5 border-b border-b-accent border-solid p-6 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'mx-1 flex grow flex-col space-y-1.5 overflow-visible overflow-y-auto px-5 py-6 text-center sm:text-left',
      //
      className
    )}
    {...props}
  />
);
DialogBody.displayName = 'DialogBody';

/**
 * Dialog footer with consistent flex layout. Border-top + padding are opt-in
 * via `bordered` (defaults to `true` to preserve current call sites). When
 * embedding the footer in a dialog whose body already has its own bordered
 * card, pass `bordered={false}` to avoid a double border.
 */
const DialogFooter = ({
  className,
  bordered = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { bordered?: boolean }) => (
  <div
    className={cn(
      'flex shrink-0 flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      bordered && 'border-t border-t-accent border-solid px-6 py-4',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn(
      'font-semibold text-[1.4rem] leading-none tracking-tight',
      className
    )}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    className={cn('text-muted-foreground text-sm', className)}
    ref={ref}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogCancel,
  DialogSubmit
};
